import {
  ComponentProps,
  FormEvent,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Fade, WithStyles, createStyles, withStyles } from '@material-ui/core';

import classNames from 'classnames';

import { DialogComposite } from '../DialogComposite';
import { useDialogContext } from '../DialogComposite/context';
import { MultiStepDialogContextProvider, Step } from './context';

const isNotEmpty = <TValue,>(
  value: TValue | undefined | null,
): value is TValue => {
  return value !== null && value !== undefined;
};

const styles = createStyles({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
    maxHeight: 'none',
    scrollbarGutter: 'stable',
  },
  rootFullScreen: {
    minHeight: 'auto',
    height: '100%',
    overflow: 'visible',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    position: 'relative',
    width: '100%',
  },
  contentFullScreen: {
    maxWidth: 700,
    width: '100%',
    margin: '0 auto',
  },
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
  closeButtonFullScreen: {
    position: 'fixed',
    top: 16,
    right: 16,
  },
});

export interface ContentHandlers {
  go: (step: string) => void;
}

export type ContentProps = WithStyles<typeof styles> &
  ComponentProps<typeof DialogComposite.Content> & {
    step: number;
    onStepChange: (step: number) => void;
  };

const Content = forwardRef<ContentHandlers, ContentProps>((props, ref) => {
  const { children, step, onStepChange, classes, ...rest } = props;
  const onSubmitProp = (rest as Record<string, unknown>).onSubmit as
    | ((event: FormEvent) => void | Promise<void>)
    | undefined;
  const { props: dialogProps } = useDialogContext();
  const isFullScreen = Boolean(dialogProps?.fullScreen);

  const [steps, setSteps] = useState<Step[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [lockedMinHeight, setLockedMinHeight] = useState<number | undefined>();
  const previousStepHeightRef = useRef<number | undefined>(undefined);
  const prevStepRef = useRef<number>(step);

  // Callback to add a step to the wizard
  const addStep = useCallback((newStep: Step) => {
    const elements = contentRef.current?.querySelectorAll('[data-step-id]');

    // Get the order of the steps from the data-step-id attribute
    const order: string[] = Array.from(elements ?? [])
      .map(el => el.getAttribute('data-step-id'))
      .filter(isNotEmpty);

    setSteps(prevSteps => {
      if (prevSteps.find(s => s.key === newStep.key)) {
        // if the step already exists, do nothing
        return prevSteps;
      }

      // add the new step to steps, keeping the order based on the data-step-id attribute
      const newSteps = [...prevSteps, newStep];
      return newSteps.sort(
        (a, b) => order.indexOf(a.key) - order.indexOf(b.key),
      );
    });

    return () => {
      setSteps(prevSteps => prevSteps.filter(s => s.key !== newStep.key));
    };
  }, []);

  useImperativeHandle(ref, () => ({
    go: (stepKey: string) => {
      const stepIndex = steps.findIndex(s => s.key === stepKey);

      if (stepIndex === -1 && process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(`Step ${stepKey} not found`);
        return;
      }

      onStepChange(stepIndex);
    },
  }));

  const onNextStep = useCallback(() => {
    if (step === steps.length - 1) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          'Attempted to go to next step when already at the last step',
        );
      }

      return;
    }

    onStepChange(step + 1);
  }, [onStepChange, step, steps]);

  const onPrevStep = useCallback(() => {
    if (step === 0) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          'Attempted to go to previous step when already at the first step',
        );
      }

      return;
    }

    onStepChange(step - 1);
  }, [onStepChange, step]);

  // When used as a form, advance to the next step on submit instead of
  // calling the consumer's onSubmit — unless we're on the last step.
  const handleFormSubmit = useCallback(
    async (e: FormEvent) => {
      if (step < steps.length - 1) {
        e.preventDefault();
        onNextStep();
        return;
      }
      await onSubmitProp?.(e);
    },
    [step, steps.length, onNextStep, onSubmitProp],
  );

  const contextValue = useMemo(
    () => ({
      addStep,
      activeStep: step,
      onStepChange,
      steps,
      onNextStep,
      onPrevStep,
    }),
    [step, onStepChange, onNextStep, onPrevStep],
  );

  // Measure container height after each render so we can use it on next step change to prevent initial jump
  useLayoutEffect(() => {
    const current = containerRef.current?.offsetHeight;
    if (current && current > 0) {
      previousStepHeightRef.current = current;
    }
  });

  // Lock min-height to previous step height during transition to avoid initial jump
  useLayoutEffect(() => {
    if (prevStepRef.current !== step) {
      const prevHeight = previousStepHeightRef.current;
      if (prevHeight && prevHeight > 0) {
        setLockedMinHeight(prevHeight);
        const timer = setTimeout(() => setLockedMinHeight(undefined), 260);
        return () => clearTimeout(timer);
      }
      prevStepRef.current = step;
    }
    return undefined;
  }, [step]);

  return (
    <MultiStepDialogContextProvider value={contextValue}>
      <DialogComposite.Content
        {...rest}
        onSubmit={onSubmitProp ? handleFormSubmit : undefined}
        className={classNames(classes.root, rest.className, {
          [classes.rootFullScreen]: isFullScreen,
        })}
        ref={contentRef}
      >
        {/* TODO: key={step} re-mounts dialog Content every step, makes Progress to flicker */}
        <Fade in key={step} timeout={300} appear>
          <div
            className={classNames(classes.contentContainer, {
              [classes.contentFullScreen]: isFullScreen,
            })}
            ref={containerRef}
            style={lockedMinHeight ? { minHeight: lockedMinHeight } : undefined}
          >
            {children}
          </div>
        </Fade>
        {/* TODO: this shouldn't be here as it is composed in Header */}
        <DialogComposite.CloseButton
          className={classNames(classes.closeButton, {
            [classes.closeButtonFullScreen]: isFullScreen,
          })}
        />
      </DialogComposite.Content>
    </MultiStepDialogContextProvider>
  );
});

Content.displayName = 'MultiStepDialog.Content';

export default withStyles(styles)(Content);
